import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Gamepad2, Menu, Trophy, User, LogOut, Crown, X, Flame,
  Brain, Sparkles, Users, Newspaper, BookOpen, Library, ArrowRight,
} from "lucide-react";

// One mega menu drives both desktop and mobile.
//
// The header used to list eight links inline and then duplicate all of them in
// a separate hamburger panel — two lists to keep in step, and a crowded bar.
// Everything now lives in this one structure and renders once. Every `to`
// below is a real route in App.js; nothing here points at a page that 404s.
const MENU_SECTIONS = [
  {
    heading: "Play",
    accent: "text-neon-cyan",
    items: [
      { to: "/games", label: "All Games", blurb: "Browse and filter every game", icon: Gamepad2 },
      { to: "/arcade", label: "Arcade", blurb: "Fast, twitchy, high-score chasing", icon: Sparkles },
      { to: "/brain-teasers", label: "Brain Teasers", blurb: "Logic, memory and IQ tests", icon: Brain },
      { to: "/quizzes", label: "Quizzes", blurb: "Personality and trivia quizzes", icon: Library },
    ],
  },
  {
    heading: "Compete",
    accent: "text-neon-lime",
    items: [
      { to: "/leaderboard", label: "Leaderboard", blurb: "Who is topping the charts", icon: Trophy },
      { to: "/party", label: "Party Live", blurb: "Play a room full of friends", icon: Users },
    ],
  },
  {
    heading: "Read",
    accent: "text-neon-yellow",
    items: [
      { to: "/news", label: "Gaming News", blurb: "Live feed with AI briefs", icon: Newspaper },
      { to: "/documentary", label: "Documentary", blurb: "Evolving topic narratives", icon: BookOpen },
      { to: "/encyclopedia", label: "Gaming Brain", blurb: "Ask the knowledge graph", icon: Brain },
    ],
  },
];

export function Navbar() {
  const { user, setShowAuth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);

  // Following a link inside the panel changes the route; close on that rather
  // than wiring an onClick onto every item.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    // pointerdown, not click: a click listener registered during the same event
    // that opened the panel fires immediately and closes it again.
    const onPointerDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setMenuOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [menuOpen]);

  return (
    <header ref={headerRef} className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-3">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group shrink-0">
          <Gamepad2 className="w-7 h-7 text-neon-cyan group-hover:rotate-12 transition-transform" />
          <span className="font-display font-black text-lg sm:text-xl uppercase tracking-tighter">
            Video<span className="text-neon-cyan">Game</span>Mania
          </span>
        </Link>

        <button
          type="button"
          data-testid="mega-menu-btn"
          aria-expanded={menuOpen}
          aria-controls="mega-menu"
          aria-haspopup="true"
          onClick={() => setMenuOpen((open) => !open)}
          className={`flex items-center gap-2 h-10 px-4 sm:px-5 border-2 font-display font-bold uppercase text-xs sm:text-sm tracking-widest rounded-none transition-colors ${
            menuOpen
              ? "bg-neon-cyan text-black border-neon-cyan"
              : "bg-transparent text-white border-zinc-700 hover:border-neon-cyan hover:text-neon-cyan"
          }`}
        >
          {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          Menu
        </button>

        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {user?.daily_streak > 0 && (
            <div
              data-testid="navbar-streak"
              title={`${user.daily_streak}-day daily streak`}
              className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/40"
            >
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="font-display font-bold text-sm text-orange-300">{user.daily_streak}</span>
            </div>
          )}

          {!user?.is_premium && (
            <Button
              onClick={() => navigate("/subscribe")}
              data-testid="go-premium-btn"
              className="hidden sm:flex bg-neon-pink text-white font-bold uppercase text-xs tracking-widest rounded-none hover:bg-white hover:text-black transition-colors"
            >
              <Crown className="w-4 h-4 mr-1" /> Premium
            </Button>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-zinc-800 transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-neon-cyan" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-neon-cyan text-black flex items-center justify-center font-bold">
                      {(user.name || user.email)[0].toUpperCase()}
                    </div>
                  )}
                  {user.is_premium && <Crown className="w-4 h-4 text-neon-yellow" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-950 border-zinc-800 text-white w-48">
                <div className="px-2 py-1.5 text-sm text-zinc-400 truncate">{user.email}</div>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  onClick={() => navigate("/profile")}
                  data-testid="menu-profile"
                  className="cursor-pointer focus:bg-zinc-800"
                >
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/leaderboard")}
                  className="cursor-pointer focus:bg-zinc-800"
                >
                  <Trophy className="w-4 h-4 mr-2" /> Leaderboard
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="logout-btn"
                  className="cursor-pointer focus:bg-zinc-800 text-neon-pink"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => setShowAuth(true)}
              data-testid="login-button"
              variant="outline"
              className="border-zinc-700 bg-transparent text-white hover:border-neon-cyan hover:text-neon-cyan rounded-none font-bold uppercase text-xs tracking-widest"
            >
              Log In
            </Button>
          )}
        </div>
      </div>

      {menuOpen && (
        <div
          id="mega-menu"
          data-testid="mega-menu"
          className="border-t-2 border-neon-cyan/30 bg-black/95 backdrop-blur-xl max-h-[calc(100vh-4rem)] overflow-y-auto"
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {MENU_SECTIONS.map((section) => (
              <div key={section.heading}>
                <h3 className={`font-display font-black uppercase text-xs tracking-[0.2em] mb-4 ${section.accent}`}>
                  {section.heading}
                </h3>
                <ul className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.to;
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          data-testid={`mega-link-${item.to.replace(/\//g, "")}`}
                          className={`group flex items-start gap-3 p-2.5 border-2 transition-colors ${
                            active
                              ? "border-neon-cyan bg-neon-cyan/5"
                              : "border-transparent hover:border-zinc-700 hover:bg-zinc-900/60"
                          }`}
                        >
                          <Icon className="w-5 h-5 mt-0.5 shrink-0 text-zinc-400 group-hover:text-neon-cyan transition-colors" />
                          <span className="min-w-0">
                            <span className="block font-display font-bold uppercase text-sm tracking-wide text-white">
                              {item.label}
                            </span>
                            <span className="block text-xs text-zinc-500">{item.blurb}</span>
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {/* One bold element on the panel, per the design guidelines. */}
            <div className="flex flex-col justify-between gap-4 bg-zinc-950 border-2 border-neon-pink p-6 neo-shadow-pink">
              <div>
                <Crown className="w-8 h-8 text-neon-yellow mb-3" />
                <h3 className="font-display font-black uppercase text-xl tracking-tight leading-none">
                  {user?.is_premium ? "You're Premium" : "Go Premium"}
                </h3>
                <p className="text-sm text-zinc-400 mt-2">
                  {user?.is_premium
                    ? "Unlimited, ad-free play. Thanks for backing the Mania."
                    : "Ad-free, unlimited plays, and your scores on the board."}
                </p>
              </div>
              <Link
                to={user?.is_premium ? "/profile" : "/subscribe"}
                data-testid="mega-premium-cta"
                className="inline-flex items-center justify-between gap-2 h-11 px-4 bg-neon-pink text-white font-display font-bold uppercase text-xs tracking-widest hover:bg-white hover:text-black transition-colors"
              >
                {user?.is_premium ? "Your Profile" : "See Plans"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {!user && (
            <div className="border-t border-zinc-800">
              <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-zinc-400">
                  Free to play. Sign in to keep going and save your scores.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowAuth(true);
                  }}
                  data-testid="mega-login-btn"
                  className="h-10 px-5 border-2 border-neon-cyan text-neon-cyan font-display font-bold uppercase text-xs tracking-widest hover:bg-neon-cyan hover:text-black transition-colors"
                >
                  Log In / Sign Up
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
